// Currency service for handling currency conversion and location-based detection

// List of supported currencies
export const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', country: 'US' },
  { code: 'EUR', symbol: '€', name: 'Euro', country: 'EU' },
  { code: 'GBP', symbol: '£', name: 'British Pound', country: 'GB' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', country: 'CA' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', country: 'AU' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', country: 'JP' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', country: 'CH' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', country: 'NO' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', country: 'SE' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', country: 'DK' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', country: 'PL' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna', country: 'CZ' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint', country: 'HU' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', country: 'BR' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso', country: 'MX' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', country: 'IN' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', country: 'CN' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won', country: 'KR' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', country: 'SG' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', country: 'HK' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', country: 'NZ' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', country: 'ZA' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira', country: 'TR' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble', country: 'RU' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', country: 'IL' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', country: 'AE' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', country: 'SA' },
  { code: 'EGP', symbol: '£', name: 'Egyptian Pound', country: 'EG' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', country: 'TH' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', country: 'MY' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso', country: 'PH' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', country: 'ID' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', country: 'VN' }
];

// Get currency by country code
export const getCurrencyByCountry = (countryCode) => {
  const currency = SUPPORTED_CURRENCIES.find(c => c.country === countryCode);
  return currency || SUPPORTED_CURRENCIES.find(c => c.code === 'NOK') || SUPPORTED_CURRENCIES[0]; // Default to NOK
};

// Get user's location and currency
export const detectUserCurrency = async () => {
  try {
    // First try IP-based detection (more reliable)
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    const countryCode = data.country_code;
    
    const currency = getCurrencyByCountry(countryCode);
    return currency;
  } catch (error) {
    
    try {
      // Fallback to geolocation
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          enableHighAccuracy: false
        });
      });

      // Reverse geocoding to get country
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
      );
      
      const data = await response.json();
      const countryCode = data.countryCode;
      
      return getCurrencyByCountry(countryCode);
    } catch (geoError) {
      // Fallback to browser locale
      const locale = navigator.language || navigator.languages[0];
      const countryCode = locale.split('-')[1];
      return getCurrencyByCountry(countryCode) || SUPPORTED_CURRENCIES.find(c => c.code === 'NOK') || SUPPORTED_CURRENCIES[0];
    }
  }
};

// Format currency amount
export const formatCurrency = (amount, currencyCode = 'NOK') => {
  const currency = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode) || SUPPORTED_CURRENCIES.find(c => c.code === 'NOK') || SUPPORTED_CURRENCIES[0];
  
  // Map currency codes to appropriate locales for proper formatting
  const currencyLocales = {
    'USD': 'en-US',
    'EUR': 'de-DE',
    'GBP': 'en-GB',
    'CAD': 'en-CA',
    'AUD': 'en-AU',
    'JPY': 'ja-JP',
    'CHF': 'de-CH',
    'NOK': 'nb-NO',
    'SEK': 'sv-SE',
    'DKK': 'da-DK',
    'PLN': 'pl-PL',
    'CZK': 'cs-CZ',
    'HUF': 'hu-HU',
    'BRL': 'pt-BR',
    'MXN': 'es-MX',
    'INR': 'en-IN',
    'CNY': 'zh-CN',
    'KRW': 'ko-KR',
    'SGD': 'en-SG',
    'HKD': 'en-HK',
    'NZD': 'en-NZ',
    'ZAR': 'en-ZA',
    'TRY': 'tr-TR',
    'RUB': 'ru-RU',
    'ILS': 'he-IL',
    'AED': 'ar-AE',
    'SAR': 'ar-SA',
    'EGP': 'ar-EG',
    'THB': 'th-TH',
    'MYR': 'ms-MY',
    'PHP': 'en-PH',
    'IDR': 'id-ID',
    'VND': 'vi-VN'
  };
  
  const locale = currencyLocales[currencyCode] || 'en-US';
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    // Fallback formatting
    return `${currency.symbol}${amount.toFixed(2)}`;
  }
};

// Convert currency (mock implementation - in real app, use real API)
export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  // Mock conversion rates (in real app, fetch from API like exchangerate-api.com)
  const mockRates = {
    'USD': 1,
    'EUR': 0.85,
    'GBP': 0.73,
    'CAD': 1.25,
    'AUD': 1.35,
    'JPY': 110,
    'NOK': 8.5,
    'SEK': 8.8,
    'DKK': 6.3,
    'PLN': 3.9,
    'CZK': 21.5,
    'HUF': 300,
    'BRL': 5.2,
    'MXN': 20,
    'INR': 75,
    'CNY': 6.4,
    'KRW': 1200,
    'SGD': 1.35,
    'HKD': 7.8,
    'NZD': 1.4,
    'ZAR': 15,
    'TRY': 8.5,
    'RUB': 75,
    'ILS': 3.2,
    'AED': 3.67,
    'SAR': 3.75,
    'EGP': 15.7,
    'THB': 33,
    'MYR': 4.2,
    'PHP': 50,
    'IDR': 14000,
    'VND': 23000
  };

  const fromRate = mockRates[fromCurrency] || 1;
  const toRate = mockRates[toCurrency] || 1;
  
  const convertedAmount = (amount / fromRate) * toRate;
  return Math.round(convertedAmount * 100) / 100; // Round to 2 decimal places
};

// Get currency symbol
export const getCurrencySymbol = (currencyCode) => {
  const currency = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
  return currency ? currency.symbol : 'kr';
};
